"use client";

import React, {
  createElement,
  Fragment,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePagination, useSearchBox } from "react-instantsearch";
import { autocomplete } from "@algolia/autocomplete-js";
import { createRoot } from "react-dom/client";
import { debounce } from "@algolia/autocomplete-shared";
import { useRouter } from "next/navigation";
import classnames from "classnames";
import { AutocompleteOptions } from "@algolia/autocomplete-js";
import { getSourcesWithFilters, Source } from "./GetSources";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";

import "@algolia/autocomplete-theme-classic";
import { SearchFiltersContext } from "@/search/SearchContext";

/* Autocomplete is used in SearchBar.tsx, and you find its configuration here, like the plugins and the autocomplete instance.

Followed this guide to build this component: https://www.algolia.com/doc/ui-libraries/autocomplete/integrations/with-react-instantsearch/?client=Autocomplete.tsx */

type UIState = { query: string };

export type AutocompleteProps = Partial<AutocompleteOptions<Source>> & {
  className?: string;
};

export function Autocomplete({
  className,
  ...autocompleteProps
}: AutocompleteProps) {
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const panelRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const rootRef = useRef<
    Element | DocumentFragment | Document | ShadowRoot | null
  >(null);
  const router = useRouter();
  const { query, refine: setQuery } = useSearchBox();
  const { refine: setPage } = usePagination();
  const { filters } = use(SearchFiltersContext);

  const [instantSearchUiState, setInstantSearchUiState] = useState<UIState>({
    query,
  });

  useEffect(() => {
    setQuery(instantSearchUiState.query);
    setPage(0);
  }, [instantSearchUiState, setPage, setQuery]);

  const debouncedSetInstantSearchUiState = useCallback(
    (state: UIState) => debounce(() => setInstantSearchUiState(state), 1000)(),
    [setInstantSearchUiState],
  );

  const getSources = getSourcesWithFilters({ filters });

  const plugins = useMemo(() => {
    // First plugin, shows recent searches
    const recentSearchPlugin = createLocalStorageRecentSearchesPlugin({
      key: "instantsearch",
      limit: 3,
      transformSource({ source }) {
        return {
          ...source,
          onSelect({ item }) {
            setInstantSearchUiState({ query: item.label });
            router.push(`/search?query=${item.label}`); // Navigate to search page with the selected recent search, works both with mouse click and keyboard enter
          },
          getItems(params) {
            if (!params.state.query) return [];
            return source.getItems(params);
          },
        };
      },
    });
    return [recentSearchPlugin];
  }, [setInstantSearchUiState, router]);

  useEffect(() => {
    if (!autocompleteContainer.current) return;
    const autocompleteInstance = autocomplete({
      ...autocompleteProps,
      container: autocompleteContainer.current,
      initialState: { query },
      plugins,
      getSources: getSources,
      onReset: () => setInstantSearchUiState({ query: "" }),
      onSubmit: ({ state }) => {
        if (state.query) router.push(`/search?query=${state.query}`);
        else router.push("/search");
      },
      onStateChange: ({ prevState, state }) => {
        if (prevState.query !== state.query)
          debouncedSetInstantSearchUiState({
            query: state.query,
          });
      },
      navigator: {
        navigate: ({ itemUrl }) => router.push(itemUrl), // Navigate to book product page with keyboard enter
      },
      renderer: {
        createElement,
        Fragment,
        render: () => {},
      },
      render: ({ children }, root) => {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;
          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }
        panelRootRef.current.render(children);
      },
    });
    return () => {
      autocompleteInstance.destroy();
    };
    // Ignorning as autocompleteProps and query will cause flickering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    plugins,
    router,
    setInstantSearchUiState,
    debouncedSetInstantSearchUiState,
  ]);

  return (
    <div
      className={classnames(
        "relative w-full",
        "w-full rounded-[1.875rem] bg-white px-4 py-3 sm:bg-[var(--bg-primary)] sm:px-0 sm:py-0",
        "[&_.aa-Form]:h-11! [&_.aa-Form]:rounded-[1.875rem]! [&_.aa-Form]:border-none! [&_.aa-Form]:bg-[#EFF0F2]! [&_.aa-Form]:text-sm!",
        "[&_.aa-Input]:text-base! [&_.aa-Input]:placeholder-[#686B72]!",
        "[&_.aa-SubmitIcon]:text-[#686B72]!",
        className,
      )}
      ref={autocompleteContainer}
    />
  );
}
